// AppDownload.jsx
import React from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Box,
  Stack,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AndroidIcon from "@mui/icons-material/Android";
import AppleIcon from "@mui/icons-material/Apple";
import DownloadIcon from "@mui/icons-material/Download";

const BINANCE_YELLOW = "#F0B90B";
const CARD_BG = "#263238";
const BORDER_COLOR = "rgba(255,255,255,0.08)";
const TEXT_MUTED = "#B0BEC5";

/**
 * Props:
 * - imageUrl?: string
 * - onAndroidClick?: () => void
 * - oniOSClick?: () => void
 * - downloadHref?: string  // رابط التحميل الفعلي (APK أو ملف آخر)
 */
export default function AppDownload({
  imageUrl,
  onAndroidClick,
  oniOSClick,
  // ضع هنا رابط الـ APK على الـ VPS
  downloadHref = "https://1cryptox.it.com/apk/1cryptox-v1.0.0.apk",
}) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm")); // <600
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md")); // 600-900
  const isMdUp = useMediaQuery(theme.breakpoints.up("md")); // >=900

  return (
    <Card
      sx={{
        bgcolor: CARD_BG,
        borderRadius: 2,
        border: `1px solid ${BORDER_COLOR}`,
        boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
        overflow: "hidden",
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Grid container spacing={{ xs: 2.5, md: 3 }} alignItems="center">
          {/* النص و الأزرار */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mb: { xs: 1, sm: 1.5 },
              }}
            >
              <Typography
                variant={isMdUp ? "h5" : "h6"}
                sx={{
                  fontWeight: 900,
                  color: "#FFFFFF",
                  letterSpacing: 0.2,
                  lineHeight: 1.15,
                }}
              >
                1CryptoX Mobile App
              </Typography>
              {/* لمسة صفراء صغيرة */}
              <Box
                sx={{
                  height: 3,
                  width: { xs: 22, sm: 24, md: 26 },
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${BINANCE_YELLOW}, rgba(240,185,11,0.25))`,
                }}
              />
            </Box>

            <Typography
              sx={{
                mb: { xs: 1.5, sm: 2 },
                color: TEXT_MUTED,
                fontSize: { xs: 13.5, sm: 14, md: 14.5 },
              }}
            >
              Trade on the go with our optimized mobile app — fast execution,
              secure login, and real-time market alerts. Early access coming
              soon on iOS and Android.
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={{ xs: 1, sm: 1.2 }}
              sx={{ flexWrap: "wrap" }}
            >
              {/* Google Play (Soon) */}
              <Button
                onClick={onAndroidClick}
                variant="contained"
                startIcon={
                  <AndroidIcon
                    sx={{
                      color: BINANCE_YELLOW,
                      filter: "drop-shadow(0 0 3px rgba(240,185,11,0.6))",
                    }}
                  />
                }
                disabled
                fullWidth={isXs}
                sx={{
                  fontWeight: 800,
                  bgcolor: "rgba(240,185,11,0.14)",
                  color: BINANCE_YELLOW,
                  border: `1px solid rgba(240,185,11,0.28)`,
                  textTransform: "none",
                  "&.Mui-disabled": {
                    bgcolor: "rgba(240,185,11,0.10)",
                    color: BINANCE_YELLOW,
                    borderColor: "rgba(240,185,11,0.22)",
                    opacity: 1,
                  },
                  "&:hover": { bgcolor: "rgba(240,185,11,0.18)" },
                }}
              >
                Google Play (Soon)
              </Button>

              {/* App Store (Soon) */}
              <Button
                onClick={oniOSClick}
                variant="outlined"
                startIcon={
                  <AppleIcon
                    sx={{
                      color: BINANCE_YELLOW,
                      filter: "drop-shadow(0 0 3px rgba(240,185,11,0.5))",
                    }}
                  />
                }
                disabled
                fullWidth={isXs}
                sx={{
                  fontWeight: 800,
                  color: "#EAEFF2",
                  borderColor: BORDER_COLOR,
                  textTransform: "none",
                  "&.Mui-disabled": {
                    color: "#EAEFF2",
                    borderColor: "rgba(255,255,255,0.18)",
                    opacity: 1,
                  },
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.35)",
                    bgcolor: "rgba(255,255,255,0.02)",
                  },
                }}
              >
                App Store (Soon)
              </Button>

              {/* Download APK */}
              <Button
                component="a"
                href={downloadHref}
                variant="contained"
                startIcon={<DownloadIcon sx={{ color: "#1C2526" }} />}
                fullWidth={isXs || isSm}
                target="_blank"
                rel="noopener noreferrer"
                // محاولة إجبار المتصفح على التعامل معه كتحميل
                download
                sx={{
                  fontWeight: 800,
                  bgcolor: BINANCE_YELLOW,
                  color: "#1C2526",
                  textTransform: "none",
                  "&:hover": { bgcolor: "#FFD54F" },
                }}
              >
                Download APK
              </Button>
            </Stack>

            <Typography
              variant="caption"
              sx={{
                mt: { xs: 1.25, sm: 1.5 },
                display: "block",
                color: TEXT_MUTED,
              }}
            >
              * Early access (closed beta). Notifications & alerts configurable
              from Settings.
            </Typography>
          </Grid>

          {/* مساحة الصورة / الموك-أب */}
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: "relative",
                // ارتفاع متكيف حسب الجهاز
                height: { xs: 220, sm: 260, md: 300 },
                borderRadius: 2,
                border: `1px solid ${BORDER_COLOR}`,
                overflow: "hidden",
                background:
                  "radial-gradient(55% 60% at 70% 35%, rgba(240,185,11,0.12) 0%, rgba(240,185,11,0.03) 60%, rgba(0,0,0,0.0) 100%), #1F2A2E",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: TEXT_MUTED,
              }}
            >
              {/* لمعان زجاجي خفيف */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "-20%",
                  width: "60%",
                  height: "200%",
                  transform: "rotate(25deg)",
                  background:
                    "linear-gradient(90deg, rgba(255,255,255,0.06), rgba(255,255,255,0))",
                }}
              />
              {imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt="App preview"
                  sx={{
                    maxHeight: "92%",
                    maxWidth: "92%",
                    objectFit: "contain",
                    zIndex: 1,
                    borderRadius: 1.5,
                    boxShadow: "0 12px 30px rgba(0,0,0,0.45)",
                  }}
                />
              ) : (
                <Box
                  sx={{
                    zIndex: 1,
                    px: { xs: 1.5, sm: 2 },
                    py: { xs: 0.75, sm: 1 },
                    borderRadius: 1.5,
                    border: `1px dashed ${BORDER_COLOR}`,
                    color: "#EAEFF2",
                    bgcolor: "rgba(0,0,0,0.15)",
                    backdropFilter: "blur(2px)",
                    fontSize: { xs: 12.5, sm: 13.5 },
                    textAlign: "center",
                  }}
                >
                  App Preview / Phone Mockup
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
